import { Observable, shareReplay, of, tap } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, inject, LOCALE_ID, Injectable, Inject } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import * as i1$1 from '@angular/cdk/platform';
import { forEach } from 'lodash-es';

class CacheItem {
    constructor(value, ttlMs = Infinity) {
        this.value = value;
        this.createdTimestamp = Date.now();
        this.expirationMs = this.createdTimestamp + ttlMs;
    }
    isExpired(ttlMsOverride) {
        if (typeof ttlMsOverride !== 'undefined') {
            return Date.now() > this.createdTimestamp + ttlMsOverride;
        }
        return Date.now() > this.expirationMs;
    }
}

class CacheCollection {
    static { this.DEFAULT_KEY = 'DEFAULT_KEY'; }
    constructor(maxAgeMs) {
        this.items = new Map();
        this.maxAgeMs = maxAgeMs ?? undefined;
    }
    /**
     * Set an item in the cache collection.
     * @param value The value to store.
     * @param key The string key to index the value with.
     * @param maxAgeMsOverride The max age in milliseconds that this value will be considered good. See {@link getItem}.
     */
    setItem(value, key = CacheCollection.DEFAULT_KEY, maxAgeMsOverride) {
        this.items.set(key, new CacheItem(value, maxAgeMsOverride ?? this.maxAgeMs));
    }
    /**
     * Clear an item from the cache collection.
     * @param key The string key matching the item to be cleared. If no key is passed, the item stored with DEFAULT_KEY
     * (if any) will be cleared.
     */
    clearItem(key = CacheCollection.DEFAULT_KEY) {
        this.items.delete(key);
    }
    /**
     * Clear all items from the cache collection.
     */
    clearAll() {
        this.items.clear();
    }
    /**
     * Get the specified cached item.
     * @param key The string key matching the item to be retrieved.
     * @param maxAgeMsOverride Override the max age the item was stored with. This allows you to get an
     * expired value, or only return a value if it is fresh.
     * @returns The cached value or null if expired/missing.
     */
    getItem(key = CacheCollection.DEFAULT_KEY, maxAgeMsOverride) {
        const item = this.items.get(key);
        if (!item || item.isExpired(maxAgeMsOverride)) {
            return null;
        }
        return item.value;
    }
    /**
     * Clear all items older than maxAgeMs from the cache collection.
     * @param maxAgeMs The max age in milliseconds for a cached item to be kept.
     */
    purgeOld(maxAgeMs) {
        for (const item of this.items) {
            if (item[1].isExpired(maxAgeMs)) {
                this.items.delete(item[0]);
            }
        }
    }
}

class CacheCollectionObservable {
    constructor(maxAgeMs) {
        this.cache = new CacheCollection(maxAgeMs);
    }
    /**
     * Set an observable value in this collection.
     * @param value Any value. Value will be wrapped in an observable when returned if not an observable already.
     * @param key String key to store and get value.
     * @param maxAgeMsOverride Milliseconds before this item expires, if different from the default for this collection.
     * @returns The original value or value wrapped in an observable. Use this returned value or get a value from the
     * collection when subscribing to ensure the cached observable is replaced after it completes.
     */
    setItem(value, key, maxAgeMsOverride) {
        if (value instanceof Observable) {
            value = value.pipe(shareReplay(1));
        }
        this.cache.setItem(value, key, maxAgeMsOverride);
        return this.getItem(key);
    }
    /**
     * Clear an item from the cache collection.
     * @param key The string key matching the item to be cleared. If no key is passed, the item stored with DEFAULT_KEY (if any) will be cleared.
     */
    clearItem(key) {
        this.cache.clearItem(key);
    }
    /**
     * Clear all items from the cache collection.
     */
    clearAll() {
        this.cache.clearAll();
    }
    /**
     * Get the specified cached item, wrapping it in an observable if it is not stored as one.
     * @param key The string key matching the item to be retrieved.
     * @param maxAgeMsOverride Override the max age the item was stored with. This allows you to get an
     * expired value, or only return a value if it is fresh.
     * @returns The cached value as an observable or null if expired/missing. If the stored value is an observable that
     * has not completed, the cached observable will be returned as is. If the original observable has completed, a new
     * self-completing observable will be returned in its place.
     */
    getItem(key, maxAgeMsOverride) {
        const cached = this.cache.getItem(key, maxAgeMsOverride);
        if (cached == null) {
            return null;
        }
        if (!(cached instanceof Observable)) {
            return of(cached);
        }
        return cached;
    }
    /**
     * Get the specified cached item, wrapping it in an observable if it is not stored as one.
     * @param getter A cold observable to get the value if it has not been cached already.
     * @param key The string key matching the item to be retrieved.
     * @param maxAgeMsOverride Override the max age the item was stored with. This allows you to get an
     * expired value, or only return a value if it is fresh.
     * @returns The cached value as an observable. If the stored value is an observable that has not completed, the cached
     * observable will be returned as is. If the original observable has completed, a new self-completing observable will
     * be returned in its place. If nothing has been cached for the specified key yet, the getter will be cached and
     * returned as the cached value.
     */
    getItemWithGetter(getter, key, maxAgeMsOverride) {
        const cached = this.getItem(key, maxAgeMsOverride);
        if (cached !== null) {
            return cached;
        }
        return this.setItem(getter, key, maxAgeMsOverride);
    }
    /**
     * Clear all items older than maxAgeMs from the cache collection.
     * @param maxAgeMs The max age in milliseconds for a cached item to be kept.
     */
    purgeOld(maxAgeMs) {
        this.cache.purgeOld(maxAgeMs);
    }
}

class CacheCollectionPromise {
    constructor(ttlMs) {
        this.cache = new CacheCollection(ttlMs);
    }
    setItem(value, key, ttlMsOverride) {
        if (value instanceof Promise) {
            this.cache.setItem(value, key, ttlMsOverride);
            return;
        }
        this.cache.setItem(Promise.resolve(value), key, ttlMsOverride);
    }
    clearItem(key) {
        this.cache.clearItem(key);
    }
    clearAll() {
        this.cache.clearAll();
    }
    getItem(key, ttlMsOverride) {
        return this.cache.getItem(key, ttlMsOverride);
    }
    purgeOld(maxAgeMs) {
        this.cache.purgeOld(maxAgeMs);
    }
}

const WINDOW = new InjectionToken('Global this, AKA window', {
    factory: () => {
        const { defaultView } = inject(DOCUMENT);
        return defaultView;
    },
});

const defaultCulture = 'en-US';
// Order of supported cultures is significant. More generic cultures serve as fallbacks.
/* eslint-disable sort-keys */
const supportedCultures = {
    'cs-cz': 'cs',
    cs: 'cs',
    'da-dk': 'da',
    da: 'da',
    'de-de': 'de',
    de: 'de',
    'el-gr': 'el',
    el: 'el',
    'en-ca': 'en-CA',
    'en-us': 'en',
    en: 'en',
    'es-mx': 'es-MX',
    'es-us': 'es-US',
    'es-es': 'es',
    es: 'es',
    'fi-fi': 'fi',
    fi: 'fi',
    'fr-ca': 'fr-CA',
    'fr-fr': 'fr',
    fr: 'fr',
    'it-it': 'it',
    it: 'it',
    'nb-no': 'nb',
    nb: 'nb',
    'nl-nl': 'nl',
    nl: 'nl',
    'pl-pl': 'pl',
    pl: 'pl',
    'pt-pt': 'pt-PT',
    'pt-br': 'pt',
    pt: 'pt',
    'ru-ru': 'ru',
    ru: 'ru',
    'sv-se': 'sv',
    sv: 'sv',
};
/* eslint-enable sort-keys */
const serviceCultures = {
    cs: 'cs',
    da: 'da',
    de: 'de',
    el: 'el',
    en: 'en-US',
    'en-CA': 'en-US',
    es: 'es-ES',
    'es-MX': 'es-MX',
    'es-US': 'es-US',
    fi: 'fi',
    fr: 'fr-FR',
    'fr-CA': 'fr-CA',
    it: 'it',
    nb: 'nb',
    nl: 'nl',
    pl: 'pl',
    pt: 'pt-BR',
    'pt-PT': 'pt-PT',
    ru: 'ru',
    sv: 'sv',
};
const urlCultures = {
    cs: 'cs-cz',
    da: 'da-dk',
    de: 'de-de',
    el: 'el-gr',
    en: 'en-us',
    'en-CA': 'en-ca',
    es: 'es-es',
    'es-MX': 'es-mx',
    'es-US': 'es-us',
    fi: 'fi-fi',
    fr: 'fr-fr',
    'fr-CA': 'fr-ca',
    it: 'it-it',
    nb: 'nb-nb',
    nl: 'nl-nl',
    pl: 'pl-pl',
    pt: 'pt-br',
    'pt-PT': 'pt-pt',
    ru: 'ru-ru',
    sv: 'sv-se',
};
function getSupportedCulture(culture) {
    return supportedCultures[culture.toLowerCase()] || 'en';
}
function getServiceCulture(culture) {
    return serviceCultures[getSupportedCulture(culture)] || defaultCulture;
}
function toUrlCulture(culture) {
    return urlCultures[culture] || defaultCulture;
}
// remove once Culture references have been replaced with individual imports
/** @deprecated - import individually instead */
//eslint-disable-next-line @typescript-eslint/no-extraneous-class
class Culture {
    static { this.defaultCulture = defaultCulture; }
    static { this.supportedCultures = supportedCultures; }
    static getSupported(culture) {
        return getSupportedCulture(culture);
    }
    static getServiceCulture(culture) {
        return getServiceCulture(culture);
    }
}

class LanguageInterceptor {
    constructor(locale) {
        this.locale = locale;
        this.cvcMicroserviceRegex = /^https:\/\/(.*).polariscvc(-dev|-stage)?.com\/.*$/;
        this.etcServiceRegex = /etc\.polaris\.com/;
        this.locale = getServiceCulture(this.locale);
    }
    intercept(req, next) {
        if (this.cvcMicroserviceRegex.test(req.url) || this.etcServiceRegex.test(req.url)) {
            // Add en-us as fallback if the locale is not en-us
            const languageWithFallback = this.locale.includes('en-US') ? this.locale : `${this.locale},en-US;q=0.9`;
            const clone = req.clone({
                headers: req.headers.set('Accept-Language', languageWithFallback),
            });
            return next.handle(clone);
        }
        return next.handle(req);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LanguageInterceptor, deps: [{ token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LanguageInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LanguageInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }] });

class WebComponentInterceptHandler {
    constructor(next, interceptor) {
        this.next = next;
        this.interceptor = interceptor;
    }
    handle(req) {
        return this.interceptor.intercept(req, this.next);
    }
}
class WebComponentInterceptorHandler {
    constructor(backend, injector, interceptors) {
        this.backend = backend;
        this.injector = injector;
        this.interceptors = interceptors;
        this.chain = null;
    }
    handle(req) {
        if (this.chain === null) {
            const interceptors = this.injector.get(this.interceptors, []);
            this.chain = interceptors.reduceRight((next, interceptor) => new WebComponentInterceptHandler(next, interceptor), this.backend);
        }
        return this.chain.handle(req);
    }
}

const WEB_COMPONENT_HTTP_INTERCEPTORS = new InjectionToken('API_HTTP_INTERCEPTORS');
class WebComponentHttpClient extends HttpClient {
    constructor(backend, injector) {
        super(new WebComponentInterceptorHandler(backend, injector, WEB_COMPONENT_HTTP_INTERCEPTORS));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: WebComponentHttpClient, deps: [{ token: i1.HttpBackend }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: WebComponentHttpClient }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: WebComponentHttpClient, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpBackend }, { type: i0.Injector }] });

class WebComponentNoInterceptInterceptHandler {
    constructor(next, interceptor) {
        this.next = next;
        this.interceptor = interceptor;
    }
    handle(req) {
        return this.interceptor.intercept(req, this.next);
    }
}
class WebComponentNoInterceptInterceptorHandler {
    constructor(backend, injector, interceptors) {
        this.backend = backend;
        this.injector = injector;
        this.interceptors = interceptors;
        this.chain = null;
    }
    handle(req) {
        if (this.chain === null) {
            const interceptors = this.injector.get(this.interceptors, []);
            this.chain = interceptors.reduceRight((next, interceptor) => new WebComponentNoInterceptInterceptHandler(next, interceptor), this.backend);
        }
        return this.chain.handle(req);
    }
}

const WEB_COMPONENT_NO_AUTH_HTTP_INTERCEPTORS = new InjectionToken('API_HTTP_INTERCEPTORS');
class WebComponentNoInterceptHttpClient extends HttpClient {
    constructor(backend, injector) {
        super(new WebComponentNoInterceptInterceptorHandler(backend, injector, WEB_COMPONENT_NO_AUTH_HTTP_INTERCEPTORS));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: WebComponentNoInterceptHttpClient, deps: [{ token: i1.HttpBackend }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: WebComponentNoInterceptHttpClient }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: WebComponentNoInterceptHttpClient, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpBackend }, { type: i0.Injector }] });

function provideCoreMocks() {
    return [];
}

class LocalStorageService {
    constructor(window) {
        this.window = window;
    }
    get(key) {
        let item;
        try {
            item = this.window.localStorage.getItem(key);
        }
        catch (error) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            console.debug(error);
            // Calling sessionStorage.get() can throw and error if blocked for security reasons (e.g. private browsing mode).
            return null;
        }
        if (!item || /^"?null"?$/i.test(item)) {
            return null;
        }
        if (!item || /^"?undefined"?$/i.test(item)) {
            return undefined;
        }
        try {
            return JSON.parse(item);
        }
        catch (error) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            console.debug(error);
            // Return the unparsed item in case it was accidentally stored without being stringified.
            return item;
        }
    }
    set(key, value) {
        try {
            this.window.localStorage.setItem(key, JSON.stringify(value));
            return true;
        }
        catch (error) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            console.debug(error);
            return false;
        }
    }
    remove(key) {
        try {
            this.window.localStorage.removeItem(key);
            return true;
        }
        catch (error) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            console.debug(error);
            return false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocalStorageService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocalStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

class SessionStorageService {
    constructor(window) {
        this.window = window;
    }
    get(key) {
        let item;
        try {
            item = this.window.sessionStorage.getItem(key);
        }
        catch (error) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            console.debug(error);
            // Calling sessionStorage.get() can throw and error if blocked for security reasons (e.g. private browsing mode).
            return null;
        }
        if (!item || /^"?null"?$/i.test(item)) {
            return null;
        }
        if (!item || /^"?undefined"?$/i.test(item)) {
            return undefined;
        }
        try {
            return JSON.parse(item);
        }
        catch (error) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            console.debug(error);
            // Return the unparsed item in case it was accidentally stored without being stringified.
            return item;
        }
    }
    set(key, value) {
        try {
            this.window.sessionStorage.setItem(key, JSON.stringify(value));
            return true;
        }
        catch (error) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            console.debug(error);
            return false;
        }
    }
    remove(key) {
        try {
            this.window.sessionStorage.removeItem(key);
            return true;
        }
        catch (error) {
            // Using console directly because core lib does not have access to ConsoleLogService.
            console.debug(error);
            return false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SessionStorageService, deps: [{ token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SessionStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SessionStorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

class UserStoreService {
    constructor(localStorageService, sessionStorageService) {
        this.localStorageService = localStorageService;
        this.sessionStorageService = sessionStorageService;
        this.userEmail = '';
    }
    setUserPrefix(userEmail) {
        this.userEmail = userEmail;
    }
    get(key) {
        let value = this.localStorageService.get(this.getPrefixedKey(key));
        if (value === undefined || value === null) {
            value = this.localStorageService.get(key);
        }
        return value;
    }
    set(key, value) {
        return this.localStorageService.set(this.getPrefixedKey(key), value);
    }
    remove(key) {
        const prefixedKey = this.getPrefixedKey(key);
        if (!this.localStorageService.get(prefixedKey)) {
            return this.localStorageService.remove(key);
        }
        else {
            return this.localStorageService.remove(prefixedKey);
        }
    }
    getFromSessionStorage(key, prefixed = true) {
        const prefixedKey = prefixed ? this.getPrefixedKey(key) : key;
        let value = this.sessionStorageService.get(prefixedKey);
        if (value === undefined || value === null) {
            value = this.sessionStorageService.get(key);
        }
        return value;
    }
    setInSessionStorage(key, value, prefixed = true) {
        const prefixedKey = prefixed ? this.getPrefixedKey(key) : key;
        this.sessionStorageService.set(prefixedKey, value);
    }
    removeInSessionStorage(key, prefixed = true) {
        const prefixedKey = prefixed ? this.getPrefixedKey(key) : key;
        this.sessionStorageService.remove(prefixedKey);
    }
    getPrefixedKey(key) {
        return this.userEmail ? `${this.userEmail}.${key}` : key;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserStoreService, deps: [{ token: LocalStorageService }, { token: SessionStorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserStoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserStoreService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: LocalStorageService }, { type: SessionStorageService }] });

class UtilService {
    constructor(window, platform) {
        this.window = window;
        this.platform = platform;
    }
    /**
     * Find the modulo of two numbers (keeping the sign of the divisor).
     * The % operator finds the remainder (keeping the sign of the dividend).
     * see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Arithmetic_Operators#Remainder_()
     * and https://en.wikipedia.org/wiki/Modulo_operation
     * @param dividend
     * @param divisor
     */
    static modulo(dividend, divisor) {
        return ((dividend % divisor) + divisor) % divisor;
    }
    // TODO: create debug service and move this there
    static debugObservable(obs, name) {
        return obs.pipe(tap({
            complete: () => {
                console.debug(`${name} completed`);
            },
            error: (err) => {
                console.debug(`${name} error`, err);
            },
            finalize: () => {
                console.debug(`${name} finalized`);
            },
            next: (val) => {
                console.debug(`${name} next`, val);
            },
            subscribe: () => {
                console.debug(`${name} subscribed`);
            },
            unsubscribe: () => {
                console.debug(`${name} unsubscribed`);
            },
        }));
    }
    static isoDateStringToDateOnly(dateString) {
        if (!dateString || dateString.length === 0) {
            return null;
        }
        return new Date(dateString.split('T')[0].replace(/-/g, '/'));
    }
    xformObjectToFormPost(data) {
        const fd = new FormData();
        if (data instanceof File) {
            fd.append(data.name || 'file', data, data.name);
        }
        else if (data instanceof Blob) {
            fd.append('file', data);
        }
        else {
            forEach(data, (value, key) => {
                if (value instanceof FileList) {
                    if (value.length === 1) {
                        fd.append(key, value[0]);
                    }
                    else {
                        forEach(value, (file, index) => {
                            fd.append(`${key}_${index}`, file);
                        });
                    }
                }
                else {
                    fd.append(key, value);
                }
            });
        }
        return fd;
    }
    blobToFormPost(blob, fileName) {
        const formData = new FormData();
        formData.append(fileName || 'file', blob, fileName);
        return formData;
    }
    dataURItoBlob(dataURI) {
        // convert base64/URLEncoded data component to raw binary data held in a string
        let byteString;
        if (dataURI.split(',')[0].includes('base64')) {
            byteString = atob(dataURI.split(',')[1]);
        }
        else {
            byteString = decodeURI(dataURI.split(',')[1]);
        }
        // separate out the mime component
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        // write the bytes of the string to a typed array
        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ia], {
            type: mimeString,
        });
    }
    fileToFormPost(file) {
        const formData = new FormData();
        formData.append(file.name || 'file', file, file.name);
        return formData;
    }
    getBrowserType() {
        const userAgent = window.navigator.userAgent.toLowerCase();
        if (this.platform.BLINK) {
            if (userAgent.includes('opr') || userAgent.includes('opera')) {
                return 'Opera';
            }
            else if (userAgent.includes('edg')) {
                return 'Edge';
            }
            else {
                return 'Chrome';
            }
        }
        else if (this.platform.FIREFOX) {
            return 'Firefox';
        }
        else if (this.platform.EDGE) {
            return 'Edge';
        }
        else if (this.platform.SAFARI) {
            return 'Safari';
        }
        else {
            return 'Unknown';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UtilService, deps: [{ token: WINDOW }, { token: i1$1.Platform }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UtilService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UtilService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i1$1.Platform }] });

let __webpack_public_path__;
var AppSkuCode;
(function (AppSkuCode) {
    AppSkuCode[AppSkuCode["SNO"] = 0] = "SNO";
    AppSkuCode[AppSkuCode["VIC"] = 1] = "VIC";
    AppSkuCode[AppSkuCode["ORV"] = 2] = "ORV";
    AppSkuCode[AppSkuCode["IND"] = 3] = "IND";
    AppSkuCode[AppSkuCode["GEM"] = 4] = "GEM";
    AppSkuCode[AppSkuCode["SLG"] = 5] = "SLG";
})(AppSkuCode || (AppSkuCode = {}));
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["Off"] = 0] = "Off";
    LogLevel[LogLevel["Error"] = 1] = "Error";
    LogLevel[LogLevel["Warn"] = 2] = "Warn";
    LogLevel[LogLevel["Info"] = 3] = "Info";
    LogLevel[LogLevel["Debug"] = 4] = "Debug";
    LogLevel[LogLevel["Trace"] = 5] = "Trace";
})(LogLevel || (LogLevel = {}));
const logLevelName = ['off', 'error', 'warn', 'info', 'debug', 'trace'];

function notificationTypeToCamelCase(type) {
    return (type.charAt(0).toLowerCase() + type.slice(1));
}

function isPermissionType(permissionType) {
    return [
        'add_vehicle_to_garage',
        'manage_maintenance',
        'manage_role_grants',
        'manage_user_grants',
        'manage_vehicle_diagnostic',
        'manage_vehicle_privacy',
        'manage_vehicle_security',
        'manage_vehicle_settings',
        'modify_vehicle_diagnostic',
        'perform_remote_operations',
        'request_ownership',
        'share_location',
        'sync_content_with_ivi',
        'transfer_ownership',
        'view_active_alerts',
        'view_current_ride',
        'view_dtc',
        'view_location',
        'view_owner_information',
        'view_subscriptions',
        'view_telemetry',
        'view_user_grants',
        'view_vehicle_diagnostic',
        'view_vehicle_privacy',
        'view_vehicle_security',
        'view_vehicle_settings',
    ].includes(permissionType);
}

var RemoteOperationCode;
(function (RemoteOperationCode) {
    RemoteOperationCode["VehicleHealthUpdate"] = "0021";
    RemoteOperationCode["VehicleLocationUpdate"] = "0022";
    RemoteOperationCode["Wake"] = "0023";
    RemoteOperationCode["Start"] = "0041";
    RemoteOperationCode["Conditioning"] = "0042";
    RemoteOperationCode["HeatedComponentControl"] = "0043";
    RemoteOperationCode["CameraViewSettings"] = "0101";
    RemoteOperationCode["CameraView"] = "0102";
    RemoteOperationCode["OtaCheck"] = "1001";
    RemoteOperationCode["OtaUserAcceptedInstall"] = "1002";
    RemoteOperationCode["RemoteServiceReport"] = "1101";
    RemoteOperationCode["GenerateHistogram"] = "1102";
    RemoteOperationCode["J1939GenericMemoryObjectWrite"] = "1103";
    RemoteOperationCode["J1939GenericMemoryObjectRead"] = "1104";
    RemoteOperationCode["UdsGenericDidRead"] = "1105";
    RemoteOperationCode["UdsGenericDidWrite"] = "1106";
    RemoteOperationCode["ClearFault"] = "1107";
    RemoteOperationCode["LogUpload"] = "1108";
    RemoteOperationCode["OtaCancel"] = "1120";
    RemoteOperationCode["OtaRecovery"] = "1121";
    RemoteOperationCode["PrivacySettingsUpdate"] = "3001";
    RemoteOperationCode["LocationAlertToggle"] = "4001";
    RemoteOperationCode["BumpAlertToggle"] = "4002";
    RemoteOperationCode["IgnitionLockToggle"] = "4003";
    RemoteOperationCode["PasscodeChange"] = "4004";
    RemoteOperationCode["PasscodeReset"] = "4005";
    RemoteOperationCode["AuthorizedKeyPresence"] = "4006";
    RemoteOperationCode["HelpAlertToggle"] = "5001";
    RemoteOperationCode["HelpAlertSafetyContactToggle"] = "5002";
    RemoteOperationCode["HelpAlertMessageAcknowledgement"] = "5003";
    RemoteOperationCode["SpeedLimiting"] = "5010";
})(RemoteOperationCode || (RemoteOperationCode = {}));
var RemoteWakeStates;
(function (RemoteWakeStates) {
    RemoteWakeStates[RemoteWakeStates["Unknown"] = 0] = "Unknown";
    RemoteWakeStates[RemoteWakeStates["Asleep"] = 1] = "Asleep";
    RemoteWakeStates[RemoteWakeStates["Waking"] = 2] = "Waking";
    RemoteWakeStates[RemoteWakeStates["Awake"] = 3] = "Awake";
})(RemoteWakeStates || (RemoteWakeStates = {}));
var RemoteWakeStatesModifier;
(function (RemoteWakeStatesModifier) {
    RemoteWakeStatesModifier[RemoteWakeStatesModifier["None"] = 0] = "None";
    RemoteWakeStatesModifier[RemoteWakeStatesModifier["Full"] = 1] = "Full";
    RemoteWakeStatesModifier[RemoteWakeStatesModifier["Limited"] = 2] = "Limited";
    RemoteWakeStatesModifier[RemoteWakeStatesModifier["PreventedByBattery"] = 3] = "PreventedByBattery";
    RemoteWakeStatesModifier[RemoteWakeStatesModifier["PreventedByConnection"] = 4] = "PreventedByConnection";
    RemoteWakeStatesModifier[RemoteWakeStatesModifier["Failure"] = 5] = "Failure";
})(RemoteWakeStatesModifier || (RemoteWakeStatesModifier = {}));
var RemoteWakeEligibleModifier;
(function (RemoteWakeEligibleModifier) {
    RemoteWakeEligibleModifier[RemoteWakeEligibleModifier["None"] = 0] = "None";
    RemoteWakeEligibleModifier[RemoteWakeEligibleModifier["TooManyRequests"] = 1] = "TooManyRequests";
})(RemoteWakeEligibleModifier || (RemoteWakeEligibleModifier = {}));

const validTrailTerrainTypes = [
    'Barren Land',
    'Cultivated Crops',
    'Deciduous Forest',
    'Developed High Intensity',
    'Developed Low Intensity',
    'Developed Medium Intensity',
    'Developed Open Space',
    'Dwarf Scrub',
    'Emergent Herbaceous Wetlands',
    'Evergreen Forest',
    'Grassland Herbaceous',
    'Hay Pasture',
    'Herbaceous',
    'Lichens',
    'Mixed Forest',
    'Moss',
    'Open Water',
    'Perennial Snow Ice',
    'Sedge Herbaceous',
    'Shrub Scrub',
    'Unclassified',
    'Woody Wetlands',
];
function getTrailTerrainTypeLabel(terrainType) {
    switch (terrainType) {
        case 'Barren Land':
            return $localize `Barren Land`;
        case 'Cultivated Crops':
            return $localize `Cultivated Crops`;
        case 'Deciduous Forest':
            return $localize `Deciduous Forest`;
        case 'Developed High Intensity':
            return $localize `Developed, High Intensity`;
        case 'Developed Low Intensity':
            return $localize `Developed, Low Intensity`;
        case 'Developed Medium Intensity':
            return $localize `Developed, Medium Intensity`;
        case 'Developed Open Space':
            return $localize `Developed, Open Space`;
        case 'Dwarf Scrub':
            return $localize `Dwarf Scrub`;
        case 'Emergent Herbaceous Wetlands':
            return $localize `Emergent Herbaceous Wetlands`;
        case 'Evergreen Forest':
            return $localize `Evergreen Forest`;
        case 'Grassland Herbaceous':
            return $localize `Grassland/Herbaceous`;
        case 'Hay Pasture':
            return $localize `Hay/Pasture`;
        case 'Herbaceous':
            return $localize `Herbaceous`;
        case 'Lichens':
            return $localize `Lichens`;
        case 'Mixed Forest':
            return $localize `Mixed Forest`;
        case 'Moss':
            return $localize `Moss`;
        case 'Open Water':
            return $localize `Open Water`;
        case 'Perennial Snow Ice':
            return $localize `Perennial Snow/Ice`;
        case 'Sedge Herbaceous':
            return $localize `Sedge/Herbaceous`;
        case 'Shrub Scrub':
            return $localize `Shrub/Scrub`;
        case 'Unclassified':
            return $localize `Unclassified`;
        case 'Woody Wetlands':
            return $localize `Woody Wetlands`;
        default:
            return terrainType;
    }
}
function getTrailTerrainTypeIcon(terrainType) {
    switch (terrainType) {
        case 'Barren Land':
            return 'pii:terrain-barren-land';
        case 'Cultivated Crops':
            return 'pii:terrain-cultivated-crops';
        case 'Deciduous Forest':
            return 'pii:terrain-deciduous-forest';
        case 'Developed High Intensity':
            return 'pii:terrain-developed-high-intensity';
        case 'Developed Low Intensity':
            return 'pii:terrain-developed-low-intensity';
        case 'Developed Medium Intensity':
            return 'pii:terrain-developed-medium-intensity';
        case 'Developed Open Space':
            return 'pii:terrain-developed-open-space';
        case 'Dwarf Scrub':
            return 'pii:terrain-dwarf-scrub';
        case 'Emergent Herbaceous Wetlands':
            return 'pii:terrain-emergent-herbaceous-wetlands';
        case 'Evergreen Forest':
            return 'pii:terrain-evergreen-forest';
        case 'Grassland Herbaceous':
        case 'Herbaceous':
            return 'pii:terrain-grassland-herbaceous';
        case 'Hay Pasture':
            return 'pii:terrain-hay-pasture';
        case 'Lichens':
            return 'pii:terrain-lichens';
        case 'Mixed Forest':
            return 'pii:terrain-mixed-forest';
        case 'Moss':
            return 'pii:terrain-moss';
        case 'Open Water':
            return 'pii:terrain-open-water';
        case 'Perennial Snow Ice':
            return 'pii:terrain-perennial-snow-ice';
        case 'Sedge Herbaceous':
            return 'pii:terrain-sedge-herbaceous';
        case 'Shrub Scrub':
            return 'pii:terrain-shrub-scrub';
        case 'Woody Wetlands':
            return 'pii:terrain-woody-wetlands';
        default:
            return '';
    }
}
function getTrailTypeName(trailType) {
    switch (trailType) {
        case 'ATV':
            return $localize `ATV`;
        case 'HLV':
            return $localize `Any`;
        case 'OHM':
            return $localize `Dirt Bike`;
        case 'ROV':
            return $localize `SxS`;
        case 'ROV50':
            return $localize `SxS<50"`;
        case 'UndesignatedPaved':
            return $localize `Designated Route Paved`;
        case 'UndesignatedUnpaved':
            return $localize `Designated Route Unpaved`;
        case 'UndesignatedUnmaintained':
            return $localize `Unmaintained`;
        case 'Trunk':
            return $localize `Trunk`;
        case 'Trail':
            return $localize `Trail`;
        case 'Club':
            return $localize `Club`;
        case 'UndesignatedUnmaintainedSnow':
            return $localize `Unmaintained`;
        default:
            return $localize `Other`;
    }
}
function getTrailTypeIcon(trailType) {
    switch (trailType) {
        case 'ATV':
            return 'pii:atv-trail';
        case 'HLV':
            return 'pii:other-trail';
        case 'OHM':
            return 'pii:ohm-trail';
        case 'ROV':
            return 'pii:rov-trail';
        case 'ROV50':
            return 'pii:rov50-trail';
        case 'UndesignatedPaved':
            return 'pii:des-paved-trail';
        case 'UndesignatedUnpaved':
            return 'pii:des-unpaved-trail';
        case 'UndesignatedUnmaintained':
            return 'pii:unmaintained-orv-trail';
        case 'Trunk':
            return 'pii:trunk-trail';
        case 'Trail':
            return 'pii:trail-trail';
        case 'Club':
            return 'pii:club-trail';
        case 'UndesignatedUnmaintainedSnow':
            return 'pii:unmaintained-sno-trail';
        default:
            return '';
    }
}

var UserContentColorHex;
(function (UserContentColorHex) {
    UserContentColorHex["Blue"] = "#0099CC";
    UserContentColorHex["Brown"] = "#7F4F04";
    UserContentColorHex["DarkBlue"] = "#0055FF";
    UserContentColorHex["DarkGreen"] = "#009900";
    UserContentColorHex["Gold"] = "#FFCC00";
    UserContentColorHex["Gray"] = "#777B82";
    UserContentColorHex["Green"] = "#26C407";
    UserContentColorHex["Orange"] = "#FF7E00";
    UserContentColorHex["Purple"] = "#9900CC";
    UserContentColorHex["Red"] = "#CC0000";
    UserContentColorHex["Salmon"] = "#FF6666";
})(UserContentColorHex || (UserContentColorHex = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { AppSkuCode, CacheCollection, CacheCollectionObservable, CacheCollectionPromise, CacheItem, Culture, LanguageInterceptor, LocalStorageService, LogLevel, RemoteOperationCode, RemoteWakeEligibleModifier, RemoteWakeStates, RemoteWakeStatesModifier, SessionStorageService, UserContentColorHex, UserStoreService, UtilService, WEB_COMPONENT_HTTP_INTERCEPTORS, WEB_COMPONENT_NO_AUTH_HTTP_INTERCEPTORS, WINDOW, WebComponentHttpClient, WebComponentInterceptorHandler, WebComponentNoInterceptHttpClient, WebComponentNoInterceptInterceptorHandler, __webpack_public_path__, defaultCulture, getServiceCulture, getSupportedCulture, getTrailTerrainTypeIcon, getTrailTerrainTypeLabel, getTrailTypeIcon, getTrailTypeName, isPermissionType, logLevelName, notificationTypeToCamelCase, provideCoreMocks, supportedCultures, toUrlCulture, validTrailTerrainTypes };
